import BrandShipping from './BrandShipping';
import ImageSlider from './ImageSilder';
import ProductDetails from './ProductDetails';
import { Card, CardBody, Col, Container, Row, Spinner, Alert, Form, FormGroup, Label, Input } from 'reactstrap';
import { Fragment } from 'react';
import React, { useState, useEffect } from 'react';
import Tablet from './Tabsets';
import { Breadcrumbs } from '../../../../AbstractElements';
import eventApi from '../../../../api/CheckInEvent';

// Date formatting functions
const formatDate = (dateString) => {
  if (!dateString) return 'Chưa xác định';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return 'Chưa xác định';
  }
};

const formatTime = (timeString) => {
  if (!timeString) return 'Chưa xác định';
  try {
    const date = new Date(timeString);
    return date.toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (error) {
    return 'Chưa xác định';
  }
};

const formatDateTime = (startTime, endTime) => {
  if (!startTime || !endTime) return 'Chưa xác định';
  try {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const startTimeStr = start.toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const endTimeStr = end.toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return `${startTimeStr} - ${endTimeStr}`;
  } catch (error) {
    return 'Chưa xác định';
  }
};

// Format event type for display
const formatEventType = (type) => {
  if (!type) return 'Chưa xác định';
  return type === 'online' ? 'Trực tuyến' : 'Trực tiếp';
};

// Format max attendees
const formatMaxAttendees = (maxAttendees) => {
  if (!maxAttendees || maxAttendees === 0) return 'Không giới hạn';
  return maxAttendees.toLocaleString('vi-VN');
};

const EventCheckinDetailManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Load events from API
  useEffect(() => {
    const loadEventData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get event ID from URL params
        const path = window.location.pathname.split("/");
        
        // Find the ID in the path (should be the last segment)
        let eventId = null;
        
        // Try to find ID in different positions
        for (let i = path.length - 1; i >= 0; i--) {
          const segment = path[i];
          const parsedId = parseInt(segment);
          if (!isNaN(parsedId) && parsedId > 0) {
            eventId = parsedId;
            break;
          }
        }
        
        
        if (eventId && !isNaN(eventId)) {
          // Load specific event by ID
          const response = await eventApi.getById(eventId);
          
          // Parse API response format for single event
          let eventData = null;
          if (response && response.success && response.data && response.data.event) {
            eventData = response.data.event;
          } else if (response && response.event) {
            eventData = response.event;
          } else if (response && !response.success) {
            eventData = response;
          } else {
            console.warn('⚠️ Unexpected single event response format:', response);
            eventData = response;
          }
          
          setSelectedEvent(eventData);
        } else {
          // If no specific event ID, load all events for selector
          const response = await eventApi.getAll();
          
          // Parse API response format
          let eventsData = [];
          if (response && response.success && response.data && response.data.events) {
            eventsData = response.data.events;
          } else if (Array.isArray(response)) {
            eventsData = response;
          } else if (response && Array.isArray(response.data)) {
            eventsData = response.data;
          } else {
            console.warn('⚠️ Unexpected response format:', response);
            eventsData = [];
          }
          
          setEvents(eventsData);
        }
      } catch (err) {
        console.error('❌ Error loading event data:', err);
        setError('Không thể tải dữ liệu sự kiện');
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs parent='Events' title='Chi tiết sự kiện' mainTitle='Chi tiết sự kiện' />
        <Container fluid={true}>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 mb-0">Đang tải sự kiện...</p>
          </div>
        </Container>
      </Fragment>
    );
  }

  // Show error state
  if (error) {
    return (
      <Fragment>
        <Breadcrumbs parent='Events' title='Chi tiết sự kiện' mainTitle='Chi tiết sự kiện' />
        <Container fluid={true}>
          <div className="py-5">
            <Alert color="danger">
              <strong>Lỗi:</strong> {error}
            </Alert>
          </div>
        </Container>
      </Fragment>
    );
  }

  // Show event selector if no specific event selected
  if (!selectedEvent && events.length > 0) {
    return (
      <Fragment>
        <Breadcrumbs parent='Events' title='Chọn sự kiện' mainTitle='Chọn sự kiện' />
        <Container fluid={true}>
          <Card>
            <CardBody>
              <h4>Chọn sự kiện để xem chi tiết</h4>
              <Form>
                <FormGroup>
                  <Label for="eventSelect">Chọn sự kiện:</Label>
                  <Input
                    type="select"
                    name="eventSelect"
                    id="eventSelect"
                    onChange={async (e) => {
                      const eventId = parseInt(e.target.value);
                      if (eventId && !isNaN(eventId)) {
                        try {
                          setLoading(true);
                          const event = await eventApi.getById(eventId);
                          setSelectedEvent(event);
                        } catch (err) {
                          console.error('❌ Error loading selected event:', err);
                          setError('Không thể tải chi tiết sự kiện');
                        } finally {
                          setLoading(false);
                        }
                      }
                    }}
                  >
                    <option value="">-- Chọn sự kiện --</option>
                    {events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.title || `Sự kiện #${event.id}`}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Form>
            </CardBody>
          </Card>
        </Container>
      </Fragment>
    );
  }

  // Show no events state
  if (!selectedEvent) {
    return (
      <Fragment>
        <Breadcrumbs parent='Events' title='Chi tiết sự kiện' mainTitle='Chi tiết sự kiện' />
        <Container fluid={true}>
          <div className="text-center py-5">
            <p>Không tìm thấy sự kiện</p>
          </div>
        </Container>
      </Fragment>
    );
  }

  // Format event data before passing to child components
  const formattedEvent = selectedEvent ? {
    ...selectedEvent,
    formattedDate: formatDate(selectedEvent.date),
    formattedStartTime: formatTime(selectedEvent.startTime),
    formattedEndTime: formatTime(selectedEvent.endTime),
    formattedDateTime: formatDateTime(selectedEvent.startTime, selectedEvent.endTime),
    formattedType: formatEventType(selectedEvent.type),
    formattedMaxAttendees: formatMaxAttendees(selectedEvent.maxAttendees),
    descriptionText: selectedEvent.description?.text || selectedEvent.description || 'Mô tả sự kiện sẽ được cập nhật sớm.'
  } : null;

  return (
    <Fragment>
      <Breadcrumbs parent='Events' title='Chi tiết sự kiện' mainTitle='Chi tiết sự kiện' />
      <Container fluid={true}>
        <div>
          <Row className='product-page-main p-0'>
            <Col xxl='4' md='6' className='box-col-12'>
              <Card>
                <CardBody>
                  <ImageSlider event={formattedEvent} />
                </CardBody>
              </Card>
            </Col>
            <ProductDetails event={formattedEvent} />
            <Col xxl="3" md="6" className='box-col-6'>
              <BrandShipping event={formattedEvent} />
            </Col>
          </Row>
        </div>
        <Tablet event={formattedEvent} />
      </Container>
    </Fragment>
  );
};

export default EventCheckinDetailManagement;
